import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "+966540423401";

function estimatePublicDurationMinutes(trip, routeEtaText) {
  if (trip.mode === "hourly") {
    return Math.max(Number(trip.duration || 4), 1) * 60;
  }
  if (routeEtaText) {
    const hourMatch = routeEtaText.match(/(\d+)\s*hr/);
    const minuteMatch = routeEtaText.match(/(\d+)\s*min/);
    const hours = hourMatch ? Number(hourMatch[1]) : 0;
    const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
    const parsed = hours * 60 + minutes;
    if (parsed > 0) return parsed;
  }
  return 120;
}

function buildGuestWhatsappLink({ booking, classification, selected }) {
  const phone = String(WHATSAPP_NUMBER).replace(/[^0-9]/g, "");
  if (!phone) return "";

  const lines = [
    "Hello BlackRide, I would like to book this trip.",
    "",
    `Guest name: ${booking.customer_name || "—"}`,
    `Phone: ${booking.customer_phone || "—"}`,
    booking.customer_whatsapp ? `WhatsApp: ${booking.customer_whatsapp}` : null,
    booking.customer_email ? `Email: ${booking.customer_email}` : null,
    `Service: ${classification?.serviceLabel || (booking.service_type === "hourly" ? "Hourly Booking" : "Drop off")}`,
    `Pickup: ${booking.pickup_location}`,
    booking.drop_location ? `Drop-off: ${booking.drop_location}` : null,
    `Date: ${booking.date}`,
    `Time: ${String(booking.time || "").slice(0, 5)}`,
    booking.service_type === "hourly" && booking.duration_hours ? `Duration: ${booking.duration_hours} hours` : null,
    `Passengers: ${booking.passengers}`,
    `Luggage: ${booking.luggage}`,
    selected?.vehicle ? `Preferred vehicle: ${selected.name} • ${selected.vehicle}` : booking.vehicle_preference ? `Preferred vehicle: ${booking.vehicle_preference}` : null,
    booking.quoted_amount ? `Indicative fare: SAR ${booking.quoted_amount}` : "Please share the quote and confirmation details.",
    booking.notes ? `Notes: ${booking.notes}` : null,
  ].filter(Boolean);

  return `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export default function BookingCheckoutDialog({
  open,
  onOpenChange,
  trip,
  selected,
  classification,
  routeDurationText,
}) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_whatsapp: "",
    customer_email: "",
    passengers: "1",
    luggage: "0",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successBooking, setSuccessBooking] = useState(null);

  const canSubmit = trip.from && trip.date && trip.time && (trip.mode === "hourly" || trip.to);

  const submitLabel = selected?.pricingType === "quote" ? "Request quote" : "Book ride";

  const summaryLines = useMemo(() => ([
    { label: "Service", value: classification?.serviceLabel || (trip.mode === "hourly" ? "Hourly Booking" : "Drop off") },
    { label: "From", value: trip.from || "—" },
    ...(trip.mode === "dropoff" ? [{ label: "To", value: trip.to || "—" }] : []),
    { label: "Date", value: trip.date || "—" },
    { label: "Time", value: trip.time || "—" },
    ...(trip.mode === "hourly" ? [{ label: "Duration", value: `${trip.duration} hours` }] : []),
    { label: "Vehicle", value: `${selected?.name || "Preferred vehicle"}${selected?.vehicle ? ` • ${selected.vehicle}` : ""}` },
  ]), [trip, selected, classification]);

  const reset = () => {
    setForm({
      customer_name: "",
      customer_phone: "",
      customer_whatsapp: "",
      customer_email: "",
      passengers: "1",
      luggage: "0",
      notes: "",
    });
    setError("");
    setSuccessBooking(null);
  };

  const handleOpenChange = (next) => {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      setError("Please complete the trip details before continuing.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const booking = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_whatsapp: form.customer_whatsapp || form.customer_phone,
        customer_email: form.customer_email,
        pickup_location: trip.from,
        drop_location: trip.mode === "dropoff" ? trip.to : null,
        pickup_place_id: trip.pickupPlace?.placeId || null,
        drop_place_id: trip.dropoffPlace?.placeId || null,
        date: trip.date,
        time: trip.time,
        service_type: trip.mode,
        vehicle_preference: selected ? `${selected.name} - ${selected.vehicle}` : null,
        passengers: Number(form.passengers || 1),
        luggage: Number(form.luggage || 0),
        duration_hours: trip.mode === "hourly" ? Number(trip.duration || 4) : null,
        estimated_duration_minutes: estimatePublicDurationMinutes(trip, routeDurationText),
        notes: form.notes,
        booking_source: "website",
        quoted_amount: selected?.pricingType === "fixed" ? Number(selected.price || 0) : null,
        booking_reference: null,
      };

      setSuccessBooking(booking);
    } catch (submitError) {
      setError(submitError.message || "We could not prepare the WhatsApp booking right now.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappLink = successBooking
    ? buildGuestWhatsappLink({ booking: successBooking, classification, selected })
    : "";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl border-white/10 bg-[#0b0b0b] text-white rounded-[28px] p-0 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-white/10 bg-[linear-gradient(180deg,#121212,#090909)]">
            <DialogHeader>
              <DialogTitle className="text-3xl font-[Playfair_Display] text-white">{submitLabel}</DialogTitle>
              <DialogDescription className="text-gray-400">
                Capture the guest details once, then continue the conversation on WhatsApp if needed.
              </DialogDescription>
            </DialogHeader>

            {successBooking ? (
              <div className="mt-8 space-y-4">
                <div className="rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-5">
                  <p className="text-sm uppercase tracking-[0.24em] text-emerald-300">Ready for WhatsApp</p>
                  <p className="mt-2 text-2xl font-semibold">Your trip details are prepared</p>
                  <p className="mt-2 text-sm text-emerald-100/80">
                    Continue on WhatsApp to send your request to the BlackRide operations team for quote confirmation, deposit instructions, and final trip coordination.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  {whatsappLink && (
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black"
                    >
                      Continue on WhatsApp
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenChange(false)}
                    className="inline-flex items-center justify-center rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="space-y-2 text-sm text-gray-300">
                    <span>Guest name</span>
                    <input value={form.customer_name} onChange={(e) => setForm((prev) => ({ ...prev, customer_name: e.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" required />
                  </label>
                  <label className="space-y-2 text-sm text-gray-300">
                    <span>Phone</span>
                    <input value={form.customer_phone} onChange={(e) => setForm((prev) => ({ ...prev, customer_phone: e.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" required />
                  </label>
                  <label className="space-y-2 text-sm text-gray-300">
                    <span>WhatsApp</span>
                    <input value={form.customer_whatsapp} onChange={(e) => setForm((prev) => ({ ...prev, customer_whatsapp: e.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" placeholder="Optional if same as phone" />
                  </label>
                  <label className="space-y-2 text-sm text-gray-300">
                    <span>Email</span>
                    <input type="email" value={form.customer_email} onChange={(e) => setForm((prev) => ({ ...prev, customer_email: e.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" />
                  </label>
                  <label className="space-y-2 text-sm text-gray-300">
                    <span>Passengers</span>
                    <input type="number" min="1" value={form.passengers} onChange={(e) => setForm((prev) => ({ ...prev, passengers: e.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" />
                  </label>
                  <label className="space-y-2 text-sm text-gray-300">
                    <span>Luggage</span>
                    <input type="number" min="0" value={form.luggage} onChange={(e) => setForm((prev) => ({ ...prev, luggage: e.target.value }))} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" />
                  </label>
                </div>
                <label className="space-y-2 text-sm text-gray-300 block">
                  <span>Notes</span>
                  <textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} rows={4} className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#C7A239]/60" placeholder="Flight number, concierge notes, special assistance, or luggage details" />
                </label>
                {error && <p className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button type="submit" disabled={loading} className="inline-flex items-center justify-center rounded-full bg-[#C7A239] px-5 py-3 text-sm font-semibold text-black disabled:opacity-60">
                    {loading ? "Preparing..." : submitLabel}
                  </button>
                  <button type="button" onClick={() => handleOpenChange(false)} className="inline-flex items-center justify-center rounded-full border border-white/10 px-5 py-3 text-sm font-semibold text-white">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="p-6 md:p-8 bg-[#111111]">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C7A239]">Trip summary</p>
            <div className="mt-5 space-y-3">
              {summaryLines.map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/8 bg-black/20 px-4 py-3">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-gray-500">{item.label}</p>
                  <p className="mt-1 text-sm text-white leading-6">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-3xl border border-white/8 bg-black/20 p-5">
              <p className="text-[11px] uppercase tracking-[0.24em] text-gray-500">Current fare</p>
              <p className="mt-2 text-3xl font-semibold text-white">
                {selected?.pricingType === "quote" ? "Request quote" : `SAR ${selected?.price ?? "—"}`}
              </p>
              <p className="mt-2 text-sm text-gray-400">
                Quotes remain manual for now, so the operations team can refine airport timing, route surcharges, and premium service notes before confirmation.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
